import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { PairingCategory } from '../pairings-data'
import { PAIRING_CATEGORIES } from '../pairings-data'
import { pairingsApi } from '../api/pairingsApi'
import { buildGoogleFontsUrl } from '../fonts'
import Header from './Header'
import StandaloneHeader from '../../font-pairings/components/Header'
import { useBasePath } from '../context/BasePathContext'

interface PairingsIndexProps {
  refreshKey?: number
}

export default function PairingsIndex({ refreshKey }: PairingsIndexProps) {
  const basePath = useBasePath()
  const isStandalone = basePath === '/font-pairings'
  const [categories, setCategories] = useState<PairingCategory[]>(PAIRING_CATEGORIES)
  const [loadedFonts, setLoadedFonts] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)

  // Fetch categories from API
  useEffect(() => {
    pairingsApi
      .listCategories()
      .then((apiCategories) => {
        if (apiCategories.length > 0) {
          setCategories(apiCategories)
        }
      })
      .catch(() => {
        // API unavailable — keep hardcoded fallback
      })
      .finally(() => {
        setLoading(false)
      })
  }, [refreshKey])

  // Load preview fonts (first pairing from each category)
  useEffect(() => {
    const previewFonts = categories.flatMap(cat => {
      const firstPairing = cat.pairings[0]
      if (!firstPairing) return []
      return [firstPairing.displayFont, firstPairing.bodyFont]
    })

    const uniqueFonts = Array.from(new Set(previewFonts))
    const fontsToLoad = uniqueFonts.filter(f => !loadedFonts.has(f))

    if (fontsToLoad.length > 0) {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = buildGoogleFontsUrl(fontsToLoad)
      document.head.appendChild(link)

      setLoadedFonts(prev => {
        const next = new Set(prev)
        fontsToLoad.forEach(f => next.add(f))
        return next
      })
    }
  }, [categories, loadedFonts])

  return (
    <div className="min-h-screen bg-gray-50">
      {isStandalone ? (
        <StandaloneHeader subtitle={`${categories.length} curated font pairing collections`} />
      ) : (
        <Header subtitle={`${categories.length} curated font pairing collections`} />
      )}

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Font Pairing Collections</h2>
          <p className="text-gray-600">
            Curated display + body font combinations for every style and mood.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-gray-200 rounded" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-24 mb-1" />
                    <div className="h-3 bg-gray-100 rounded w-16" />
                  </div>
                </div>
                <div className="h-3 bg-gray-100 rounded w-full mb-4" />
                <div className="h-5 bg-gray-100 rounded w-32 mb-1" />
                <div className="h-4 bg-gray-100 rounded w-24" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {categories.map((category) => (
              <CategoryCard
                key={category.id}
                category={category}
                basePath={basePath}
                isLoaded={category.pairings[0] ? loadedFonts.has(category.pairings[0].displayFont) : false}
              />
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-gray-200 mt-8">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <p className="text-sm text-gray-500 text-center">
            Powered by{' '}
            <a
              href="https://fonts.google.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-700 hover:text-gray-900 underline"
            >
              Google Fonts
            </a>
            {' '}· Built by{' '}
            <a href="/" className="text-gray-700 hover:text-gray-900 underline">
              Nimble Labs
            </a>
          </p>
        </div>
      </footer>
    </div>
  )
}

interface CategoryCardProps {
  category: PairingCategory
  basePath: string
  isLoaded: boolean
}

function CategoryCard({ category, basePath, isLoaded }: CategoryCardProps) {
  const firstPairing = category.pairings[0]
  if (!firstPairing) return null

  return (
    <Link
      to={`${basePath}/${category.id}`}
      className="block bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg hover:border-gray-300 transition-all group"
    >
      <div className="flex items-center gap-3 mb-3">
        <span className="text-2xl" role="img" aria-label={category.name}>
          {category.emoji}
        </span>
        <div>
          <h3 className="font-semibold text-gray-900 group-hover:text-gray-700 transition-colors">
            {category.name}
          </h3>
          <p className="text-xs text-gray-500">{category.pairings.length} pairings</p>
        </div>
      </div>

      <p className="text-sm text-gray-600 mb-4">{category.description}</p>

      {/* Preview */}
      <div
        className="space-y-1 transition-opacity duration-300"
        style={{ opacity: isLoaded ? 1 : 0.5 }}
      >
        <div
          className="text-lg text-gray-900 truncate"
          style={{
            fontFamily: isLoaded ? `'${firstPairing.displayFont}', sans-serif` : 'sans-serif',
          }}
        >
          {firstPairing.displayFont}
        </div>
        <div
          className="text-sm text-gray-600 truncate"
          style={{
            fontFamily: isLoaded ? `'${firstPairing.bodyFont}', sans-serif` : 'sans-serif',
          }}
        >
          + {firstPairing.bodyFont}
        </div>
      </div>

      <div className="mt-4 flex items-center text-xs text-gray-400 group-hover:text-gray-600 transition-colors">
        <span>View pairings</span>
        <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </Link>
  )
}
